import { Checkbox } from '@heroui/react';
import { faceAreas, wrappedSurfaceSqft, type BoxDims, type FaceSelection } from '@estimator/shared';

export type CoverageProductType =
  | 'banquette_base'
  | 'banquette_back_cushion'
  | 'banquette_seat_cushion'
  | 'banquette_arm';

type FaceKey = keyof FaceSelection;

const FACE_ORDER: FaceKey[] = ['front', 'back', 'top', 'bottom', 'left', 'right'];

/**
 * Per-product labels. Generic axis names are unusable on their own — "front"
 * means nothing until you know what you are looking at — so each product type
 * names its faces after what they physically are.
 */
const LABELS: Record<CoverageProductType, Record<FaceKey, string>> = {
  banquette_base: {
    front: 'Front kick', back: 'Wall side', top: 'Seat deck',
    bottom: 'Floor side', left: 'Left end', right: 'Right end',
  },
  banquette_back_cushion: {
    front: 'Face', back: 'Back', top: 'Top edge',
    bottom: 'Bottom edge', left: 'Left end', right: 'Right end',
  },
  banquette_seat_cushion: {
    front: 'Front edge', back: 'Back edge', top: 'Seat top',
    bottom: 'Underside', left: 'Left end', right: 'Right end',
  },
  banquette_arm: {
    front: 'Front cap', back: 'Rear cap', top: 'Top',
    bottom: 'Bottom', left: 'Outside face', right: 'Inside face',
  },
};

/** Guidance only — never forces a value. */
const HINTS: Record<CoverageProductType, Partial<Record<FaceKey, string>>> = {
  banquette_base: {
    back: 'usually against the wall',
    top: 'usually hidden under the seat cushion',
    bottom: 'usually on the floor',
  },
  banquette_back_cushion: { back: 'often backing fabric or uncovered' },
  banquette_seat_cushion: { back: 'often against the back cushion' },
  banquette_arm: { bottom: 'usually the mounting face' },
};

interface Props {
  productType: CoverageProductType;
  /** Dimensions in inches. Drives the per-face areas shown next to each toggle. */
  dims: BoxDims;
  value: FaceSelection;
  onChange: (next: FaceSelection) => void;
}

export default function FabricCoverageSection({ productType, dims, value, onChange }: Props) {
  const areas = faceAreas(dims);
  const labels = LABELS[productType];
  const hints = HINTS[productType];
  const totalSqft = wrappedSurfaceSqft(dims, value);

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-xs font-semibold tracking-wide text-[var(--text-secondary)] uppercase">
          Fabric Coverage
        </h3>
        <span className="text-xs text-[var(--text-secondary)]">
          {totalSqft.toFixed(2)} sqft covered
        </span>
      </div>
      <p className="text-xs text-[var(--text-secondary)]">
        Which surfaces receive fabric. Drives fabric cutting, sewing, and
        upholstery mounting labor. Deselect anything that stays uncovered.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {FACE_ORDER.map((face) => (
          <Checkbox
            key={face}
            isSelected={value[face]}
            onValueChange={(checked) => onChange({ ...value, [face]: checked })}
          >
            <span className="text-sm">{labels[face]}</span>
            <span className="ml-2 text-xs text-[var(--text-secondary)]">
              {(areas[face] / 144).toFixed(2)} sqft
              {hints[face] ? ` — ${hints[face]}` : ''}
            </span>
          </Checkbox>
        ))}
      </div>
    </div>
  );
}
